import { Plus, Users, FileText, ListTodo, DollarSign, User, Puzzle, Filter, ShoppingCart, NotebookPen, UsersRound } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../Components/UI/button";
import ConnectionEntryDialog from "../../Connections/ConnectionEntry";
import ThreadEntry from "../../Threads/ThreadEntry";
import TaskEntry from "../../Tasks/TaskEntry";
import UserEntry from "../../Users/UserEntry";
import ExpenseEntry from "../../Expenses/ExpenseEntry";
import Integrations from "../../Threads/Integrations";
import { checkUserRights, getCurrentUser } from "../../../Utils/Utilities";
import { checkIfInqOnlyMode, userIsAdmin } from "../../../Store/Reducers";
import { useSelector } from "react-redux";
import RecordEntry from "../../Records/RecordsEntry";
import { isSmartSellerMode } from "../../../Store/Reducers";
import useIsMobile from "../../../Hooks/useIsMobile"
import { SHOW_EXPENSES } from "../../../Utils/FeatureFlags";

const NextAction = () => {
    const navigate = useNavigate();
    const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

    const [isThreadEntryOpen, setIsThreadEntryOpen] = useState(false);
    const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
    const [isInteractionDialogOpen, setIsInteractionDialogOpen] = useState(false);
    const [isUserEntryOpen, setIsUserEntryOpen] = useState(false);
    const [isExpenseEntryOpen, setIsExpenseEntryOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [selectedThreadTypeID, setSelectedThreadTypeID] = useState(1);
    
    const userRights = checkUserRights();
    const currentUser = getCurrentUser();
    const isAdmin = useSelector(userIsAdmin);
    const isMobile = useIsMobile();

    const mobileActionButtons = [
        {
            id: 'user',
            label: 'Users',
            icon: User,
            onClick: () => navigate('/users'),
            show: isAdmin && !isSmartSellerMode()
        },
        {
            id: 'contact',
            label: 'Contacts',
            icon: UsersRound,
            onClick: () => navigate('/connections'),
            show: true
        },
        {
            id: 'inquiry',
            label: 'Inquiries',
            icon: Filter,
            onClick: () => navigate('/threads?tid=1'),
            show: true
        },
        {
            id: 'order',
            label: 'Sales',
            icon: ShoppingCart,
            onClick: () => navigate('/threads-report?tID=2'),
            show: true
        },
        {
            id: 'task',
            label: 'Tasks',
            icon: ListTodo,
            onClick: () => navigate('/tasks'),
            show: false
        },
        {
            id: 'record',
            label: 'Records',
            icon: NotebookPen,
            onClick: () => setIsInteractionDialogOpen(true),
            show: false
        }
    ];

    const desktopActionButtons = [
        {
            id: 'inquiry',
            label: 'Inquiry',
            icon: FileText,
            onClick: () => {
                setSelectedThreadTypeID(1);
                setIsThreadEntryOpen(true);
            },
            show: userRights.addThr
        },
        {
            id: 'order',
            label: 'Sale',
            icon: FileText,
            onClick: () => {
                setSelectedThreadTypeID(2);
                setIsThreadEntryOpen(true);
            },
            show: userRights.addThr
        },
        {
            id: 'contact',
            label: 'Contact',
            icon: Users,
            onClick: () => setIsConnectionDialogOpen(true),
            show: userRights.addConns
        },
        {
            id: 'task',
            label: 'Task',
            icon: ListTodo,
            onClick: () => setIsTaskDialogOpen(true),
            show: !isMobile
        },
        {
            id: 'record',
            label: 'Record',
            icon: ListTodo,
            onClick: () => setIsInteractionDialogOpen(true),
            show: true
        },
        {
            id: 'user',
            label: 'User',
            icon: User,
            onClick: () => setIsUserEntryOpen(true),
            show: isAdmin && !isSmartSellerMode() && !isMobile
        },
        {
            id: 'expense',
            label: 'Expense',
            icon: DollarSign,
            onClick: () => setIsExpenseEntryOpen(true),
            show: SHOW_EXPENSES() && (currentUser?.AgnConfig || userRights?.agnConfig) && !isMobile
        },
        {
            id: 'integration',
            label: 'Integration',
            icon: Puzzle,
            onClick: () => setImportDialogOpen(true),
            show: false // userRights.addThr && !isMobile
        }
    ];

    if (isMobile) {
        const visibleButtons = mobileActionButtons.filter(button => button.show);
        const gridColsClass = visibleButtons.length === 1 
            ? "grid-cols-1" 
            : visibleButtons.length === 2 
                ? "grid-cols-2" 
                : visibleButtons.length === 3 
                    ? "grid-cols-3" 
                    : "grid-cols-4";

        return (
            <>
                <div className="w-full h-full bg-white rounded-xl px-[10px] pt-[10px] pb-[10px] flex flex-col gap-3">
                    <span className="text-text-color text-[18px] font-semibold text-left">
                        Quick Access
                    </span>
                    <div className={`grid ${gridColsClass} gap-[10px] w-full`}>
                        {visibleButtons.map((button) => {
                            const IconComponent = button.icon;
                            return (
                                <button
                                    key={button.id}
                                    type="button"
                                    onClick={button.onClick}
                                    className="flex flex-col items-start justify-start gap-[10px] px-[10px] pt-[10px] pb-[7px] rounded-[7px] bg-[#0D1F2D08] border border-[#0D1F2D20] text-left active:scale-95 transition-all duration-200 w-full"
                                >
                                    <IconComponent className="h-5 w-5 text-[#0D1F2D]" strokeWidth={1.8} />
                                    <span className="text-xs font-semibold text-[#0D1F2D]">{button.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* All Dialog Components */}
                <ConnectionEntryDialog
                    open={isConnectionDialogOpen}
                    onOpenChange={setIsConnectionDialogOpen}
                    triggerButton={false}
                />
                <ThreadEntry
                    open={isThreadEntryOpen}
                    onOpenChange={setIsThreadEntryOpen}
                    triggerButton={false}
                    threadTypeID={selectedThreadTypeID}
                    onThreadUpdated={() => setIsThreadEntryOpen(false)}
                    calledFrom={(selectedThreadTypeID === 2 && isInqOnlyMode) ? "ORDDONE" : ""}
                />
                <TaskEntry
                    open={isTaskDialogOpen}
                    onOpenChange={setIsTaskDialogOpen}
                    triggerButton={false}
                />
                <UserEntry
                    isOpen={isUserEntryOpen}
                    setIsOpen={setIsUserEntryOpen}
                    userData={null}
                />
                <ExpenseEntry
                    open={isExpenseEntryOpen}
                    onOpenChange={setIsExpenseEntryOpen}
                    triggerButton={false}
                />
                <Integrations
                    open={importDialogOpen}
                    onOpenChange={setImportDialogOpen}
                    triggerButton={false}
                />
                <RecordEntry
                    triggerButton={false}
                    open={isInteractionDialogOpen}
                    onOpenChange={setIsInteractionDialogOpen}
                />
            </>
        );
    }

    return (
        <>
            <div className="w-full h-full">
                <div className="bg-bg-white rounded-xl md:border md:border-sahi-border-color md:shadow-md border-none shadow-none h-full flex flex-col md:pb-2 pb-0.5">
                    {/* Header */}
                    <div className="flex items-center justify-between gap-2 p-3 px-4 md:px-5 pb-3">
                        <span className="text-text-color text-lg sm:text-xl font-semibold">
                            Quick Actions
                        </span>
                    </div>

                    {/* Action Buttons Grid - FIXED: Only one .map() block now */}
                    <div className="flex-1 p-3 px-3 md:px-5 pt-0">
                        <div className="flex flex-wrap gap-2">
                            {desktopActionButtons.filter(button => button.show).map((button) => (
                                <Button
                                    key={button.id}
                                    variant="secondary"
                                    // Responsive sizing: h-8/text-xs on mobile, standard on desktop
                                    className="h-8 md:h-10 text-xs md:text-sm flex items-center gap-1 p-2 px-2 border border-sahi-border-color bg-sahi-disable shadow-sm hover:bg-sahi-blue-light hover:text-sahi-primary-color hover:border-transparent transition-all"
                                    onClick={button.onClick}
                                >
                                    <Plus className="h-3 w-3 md:h-4 md:w-4" />
                                    {button.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* All Dialog Components */}
            <ConnectionEntryDialog
                open={isConnectionDialogOpen}
                onOpenChange={setIsConnectionDialogOpen}
                triggerButton={false}
            />
            <ThreadEntry
                open={isThreadEntryOpen}
                onOpenChange={setIsThreadEntryOpen}
                triggerButton={false}
                threadTypeID={selectedThreadTypeID}
                onThreadUpdated={() => setIsThreadEntryOpen(false)}
                calledFrom={(selectedThreadTypeID === 2 && isInqOnlyMode) ? "ORDDONE" : ""}
            />
            <TaskEntry
                open={isTaskDialogOpen}
                onOpenChange={setIsTaskDialogOpen}
                triggerButton={false}
            />
            <UserEntry
                isOpen={isUserEntryOpen}
                setIsOpen={setIsUserEntryOpen}
                userData={null}
            />
            <ExpenseEntry
                open={isExpenseEntryOpen}
                onOpenChange={setIsExpenseEntryOpen}
                triggerButton={false}
            />
            <Integrations
                open={importDialogOpen}
                onOpenChange={setImportDialogOpen}
                triggerButton={false}
            />
            <RecordEntry
                triggerButton={false}
                open={isInteractionDialogOpen}
                onOpenChange={setIsInteractionDialogOpen}
            />
        </>
    );
};

export default NextAction;